/**
 * Firebase Configuration
 *
 * SETUP INSTRUCTIONS:
 * 1. Create a Firebase project at https://console.firebase.google.com
 * 2. Add iOS and Android apps to your project
 * 3. Download google-services.json (Android) and GoogleService-Info.plist (iOS)
 * 4. Place them in the project root directory (same level as app.json)
 * 5. Run: npx expo prebuild (this copies the files to native directories)
 * 6. Enable Authentication (Email, Google, Apple Sign-In)
 * 7. Create Firestore database
 * 8. Create Storage bucket
 * 9. Enable Analytics, Crashlytics, and Cloud Messaging
 *
 * GOOGLE SIGN IN SETUP:
 * 1. In Firebase Console, go to Authentication > Sign-in method
 * 2. Enable Google Sign-In
 * 3. Copy the Web client ID (Web SDK configuration)
 * 4. Paste it in the GOOGLE_WEB_CLIENT_ID constant below
 *
 * APPLE SIGN IN SETUP (iOS only):
 * 1. In Firebase Console, enable Apple Sign-In
 * 2. In Xcode, add Sign In with Apple capability to your project
 * 3. The @invertase/react-native-apple-authentication package will handle the rest
 *
 * React Native Firebase automatically reads configuration from:
 * - GoogleService-Info.plist (iOS)
 * - google-services.json (Android)
 * No .env config needed for Firebase!
 */

// Google Sign In Client IDs (extracted from google-services.json and GoogleService-Info.plist)
export const GOOGLE_WEB_CLIENT_ID = '861411997509-4vbrp197lem8l5ebt3ke5c6kc5v42mk6.apps.googleusercontent.com';
export const GOOGLE_IOS_CLIENT_ID = '861411997509-ua64brdhjlseorulfmnfff9vv9mv6si3.apps.googleusercontent.com';
export const GOOGLE_ANDROID_CLIENT_ID = '861411997509-6fcrbipo7seuleq914e1k75dt70rgrcr.apps.googleusercontent.com';

// Firestore collection names for Glycemic Snap (fsnap_ prefix)
export const COLLECTIONS = {
  USERS: 'fsnap_users',
  MEALS: 'fsnap_meals',
  PREFERENCES: 'fsnap_preferences',
  SUBSCRIPTIONS: 'fsnap_subscriptions',
  INSIGHTS: 'fsnap_insights',
  IMPORTED_RECIPES: 'fsnap_imported_recipes',
  DIARY_ENTRIES: 'fsnap_diary_entries',
  GLUCOSE_READINGS: 'fsnap_glucose_readings',
} as const;

// Legacy collection names (for migration purposes)
export const LEGACY_COLLECTIONS = {
  USER_SETTINGS: 'userSettings',
  LOG_ENTRIES: 'logEntries',
  PRESETS: 'presets',
  MESSAGES: 'messages',
  FAVORITES: 'favorites',
  RECENT_BARCODES: 'recentBarcodes',
  RECENTLY_USED: 'recentlyUsed',
  RECENTLY_ADDED: 'recentlyAdded',
} as const;

// Storage paths for Glycemic Snap meal photos
export const STORAGE_PATHS = {
  MEAL_PHOTOS: (userId: string, mealId: string) => `fsnap_meals/${userId}/${mealId}/photo.jpg`,
  MEAL_THUMBNAILS: (userId: string, mealId: string) => `fsnap_meals/${userId}/${mealId}/thumbnail.jpg`,
  IMPORTED_RECIPE_PHOTOS: (userId: string, recipeId: string) => `fsnap_imported_recipes/${userId}/${recipeId}/photo.jpg`,
  IMPORTED_RECIPE_THUMBNAILS: (userId: string, recipeId: string) => `fsnap_imported_recipes/${userId}/${recipeId}/thumbnail.jpg`,
  FAVORITE_PHOTOS: (userId: string, favoriteId: string) => `fsnap_favorites/${userId}/${favoriteId}/photo.jpg`,
  FAVORITE_THUMBNAILS: (userId: string, favoriteId: string) => `fsnap_favorites/${userId}/${favoriteId}/thumbnail.jpg`,
} as const;

// Analytics event names
export const ANALYTICS_EVENTS = {
  // Auth
  SIGN_UP: 'sign_up',
  SIGN_IN: 'sign_in',
  SIGN_OUT: 'sign_out',

  // Onboarding
  WELCOME_SCREEN_VIEWED: 'welcome_screen_viewed',
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_STEP_VIEWED: 'onboarding_step_viewed',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_STEP_SKIPPED: 'onboarding_step_skipped',
  ONBOARDING_BACK_TAPPED: 'onboarding_back_tapped',
  ONBOARDING_ABANDONED: 'onboarding_abandoned',
  ONBOARDING_RESUMED: 'onboarding_resumed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  ONBOARDING_PAYWALL_VIEWED: 'onboarding_paywall_viewed',
  ONBOARDING_PAYWALL_SKIP_SHOWN: 'onboarding_paywall_skip_shown',
  ONBOARDING_PAYWALL_SKIP_TAPPED: 'onboarding_paywall_skip_tapped',
  ONBOARDING_PAYWALL_PREMIUM: 'onboarding_paywall_premium',
  ONBOARDING_PAYWALL_FREE: 'onboarding_paywall_free',

  // Legacy User Migration
  LEGACY_USER_DETECTED: 'legacy_user_detected',
  LEGACY_USER_MIGRATED: 'legacy_user_migrated',
  LEGACY_MIGRATION_FAILED: 'legacy_migration_failed',

  // Camera & Meals
  PHOTO_CAPTURED: 'photo_captured',
  PHOTO_UPLOADED: 'photo_uploaded',
  MEAL_ANALYZED: 'meal_analyzed',
  MEAL_SAVED: 'meal_saved',

  // Chat
  FOLLOW_UP_QUESTION: 'follow_up_question',
  QUICK_ACTION_TAPPED: 'quick_action_tapped',

  // Symptoms
  SYMPTOM_LOGGED: 'symptom_logged',
  SYMPTOM_REMINDER_SENT: 'symptom_reminder_sent',
  SYMPTOM_REMINDER_OPENED: 'symptom_reminder_opened',

  // Premium
  PAYWALL_VIEWED: 'paywall_viewed',
  SUBSCRIPTION_STARTED: 'subscription_started',
  SCAN_LIMIT_REACHED: 'scan_limit_reached',

  // History
  HISTORY_VIEWED: 'history_viewed',
  MEAL_DETAIL_VIEWED: 'meal_detail_viewed',
  HISTORY_SEARCHED: 'history_searched',

  // Recipe Import
  RECIPE_IMPORTED: 'recipe_imported',
  IMPORTED_RECIPE_VIEWED: 'imported_recipe_viewed',

  // Weight
  WEIGHT_LOGGED: 'weight_logged',

  // Diary
  DIARY_ENTRY_ADDED: 'diary_entry_added',
  DIARY_ENTRY_EDITED: 'diary_entry_edited',
  DIARY_ENTRY_DELETED: 'diary_entry_deleted',
  DIARY_ENTRY_COPIED: 'diary_entry_copied',
  DIARY_DAY_COPIED: 'diary_day_copied',
  DIARY_HISTORY_LOCKED: 'diary_history_locked',

  // Scanning modes
  LABEL_SCANNED: 'label_scanned',
  BARCODE_SCANNED: 'barcode_scanned',

  // Favorites
  FAVORITE_SAVED: 'favorite_saved',
  FAVORITE_REMOVED: 'favorite_removed',
  FAVORITE_RELOGGED: 'favorite_relogged',

  // Streaks
  STREAK_MILESTONE: 'streak_milestone',

  // Settings engagement
  GLYCEMIC_TARGETS_CUSTOMIZED: 'glycemic_targets_customized',
  OPTIONAL_NUTRIENT_TOGGLED: 'optional_nutrient_toggled',
  NUTRIENT_TILE_TAPPED: 'nutrient_tile_tapped',

  // Blood glucose
  GLUCOSE_READING_LOGGED: 'glucose_reading_logged',

  // Daily scan limit
  DAILY_SCAN_LIMIT_REACHED: 'daily_scan_limit_reached',
} as const;
